import { exec } from 'child_process';
import util from 'util';
import logger from './logger.js';

// Promisify exec
const execPromise = util.promisify(exec);

/**
 * Run an Artillery test with the given configuration file
 * @param configFile Path to Artillery YAML configuration file
 * @param outputFile Path to save the test results (optional)
 * @returns Result of the test execution
 */
export async function runArtilleryTest(configFile: string, outputFile?: string): Promise<string> {
    try {
        let command = `npx artillery run ${configFile}`;

        if (outputFile) {
            command += ` -o ${outputFile}`;
        }

        logger.info(`Executing command: ${command}`);
        const { stdout, stderr } = await execPromise(command);

        if (stderr) {
            logger.warn(`Command stderr: ${stderr}`);
        }

        return stdout;
    } catch (error: any) {
        logger.error(`Error running Artillery test: ${error.message}`);
        throw new Error(`Failed to run Artillery test: ${error.message}`);
    }
}

/**
 * Generate a report from Artillery test results
 * @param inputFile Path to Artillery JSON test results
 * @param outputFile Path to save the HTML report
 * @returns Result of the report generation
 */
export async function generateArtilleryReport(inputFile: string, outputFile: string): Promise<string> {
    try {
        const command = `npx artillery report --output ${outputFile} ${inputFile}`;

        logger.info(`Generating report: ${command}`);
        const { stdout, stderr } = await execPromise(command);

        if (stderr) {
            logger.warn(`Report generation stderr: ${stderr}`);
        }

        return stdout;
    } catch (error: any) {
        logger.error(`Error generating Artillery report: ${error.message}`);
        throw new Error(`Failed to generate Artillery report: ${error.message}`);
    }
}

/**
 * Parse Artillery test results from a JSON file
 * @param resultData JSON result data from Artillery test
 * @returns Parsed summary of test results
 */
export async function parseArtilleryResults(resultData: any): Promise<any> {
    try {
        // Load JSON from file if needed
        if (typeof resultData === 'string') {
            try {
                resultData = JSON.parse(resultData);
            } catch (e) {
                const fs = await import('fs/promises');
                const fileContent = await fs.readFile(resultData, 'utf-8');
                resultData = JSON.parse(fileContent);
            }
        }

        const aggregate = resultData.aggregate;
        if (!aggregate) throw new Error('Invalid Artillery results: missing aggregate data');

        // Summarize
        const counters = aggregate.counters || {};
        const vusersCreated = counters['vusers.created'] || 0;
        const vusersFailed = counters['vusers.failed'] || 0;
        const vusersCompleted = counters['vusers.completed'] || 0;
        const errorKeys = Object.keys(counters).filter(k => k.startsWith('errors.'));
        const errors: Record<string, number> = {};
        errorKeys.forEach(k => {
            const label = k.replace('errors.', '');
            errors[label] = counters[k];
        });

        const durationMs = (aggregate.lastCounterAt || 0) - (aggregate.firstCounterAt || 0);
        const durationSec = durationMs > 0 ? (durationMs / 1000) : 0;

        // No HTTP status codes or response times in this result
        const summary = [
            `Test duration: ${durationSec.toFixed(2)} seconds`,
            `Virtual users created: ${vusersCreated}`,
            `Virtual users failed: ${vusersFailed}`,
            `Virtual users completed: ${vusersCompleted}`,
            errorKeys.length
                ? `Errors:\n${Object.entries(errors).map(([k, v]) => `  - ${k}: ${v}`).join('\n')}`
                : 'No errors detected.',
        ].join('\n');

        return {
            timestamp: new Date().toISOString(),
            duration: durationMs,
            vusers: {
                created: vusersCreated,
                completed: vusersCompleted,
                failed: vusersFailed,
            },
            errors,
            summary,
            resultType: vusersFailed === 0
                ? 'success'
                : (vusersCompleted > 0 ? 'partial_failure' : 'complete_failure'),
        };
    } catch (error: any) {
        logger.error(`Error parsing Artillery results: ${error.message}`);
        throw new Error(`Failed to parse Artillery results: ${error.message}`);
    }
}

/**
 * Generate a human-readable summary from the parsed metrics
 * @param metrics Parsed Artillery metrics
 * @returns Human-readable summary string
 */
function generateHumanReadableSummary(metrics: Record<string, any>): string {
    if (!metrics) {
        return "No metrics data available";
    }

    let summary = [];

    // Test duration
    const durationSec = metrics.duration ? Math.round(metrics.duration / 1000) : 0;
    summary.push(`Test duration: ${durationSec} seconds`);

    // Virtual users
    const vusers = metrics.vusers || { created: 0, completed: 0, failed: 0 };
    summary.push(`Virtual users: ${vusers.created} created, ${vusers.completed} completed, ${vusers.failed} failed`);

    // Success rate
    const successRate = metrics.successRate !== undefined ?
        `${parseFloat(metrics.successRate.toFixed(2))}%` : "N/A";
    summary.push(`Success rate: ${successRate}`);

    // Requests per second
    const requestRate = metrics.requestsPerSecond !== undefined ?
        `${parseFloat(metrics.requestsPerSecond.toFixed(2))} req/sec` : "N/A";
    summary.push(`Request rate: ${requestRate}`);

    // Add error information if there are errors
    if (metrics.errors && Object.keys(metrics.errors).length > 0) {
        summary.push(`Errors detected:`);
        Object.entries(metrics.errors).forEach(([errorType, count]) => {
            summary.push(`  - ${errorType}: ${count}`);
        });
    }

    return summary.join('\n');
}
